import { configureStore, createAction } from '@reduxjs/toolkit';
import operationReducer from './operationSlice';
import modsReducer from './modsSlice'
import selectionReducer from './selectionSlice'
import gizmoReducer from './gizmoSlice'
import snapReducer from './snapSlice'
import dxfReducer from './dxfSlice'
import layerReducer from './layerSlice'
import operationHistoryReducer from './operationHistorySlice'
import groundingReducer from './groundingSlice'; // ✅ GROUNDING SLICE
import simpleSupplyReducer from './simpleSupplySlice'; // ✅ NEW SIMPLE SUPPLY
import utilsReducer from './utilsSlice';
import settingsReducer from './settingsSlice';
import floorPlanReducer from './floorPlanSlice'
import wireReducer from './wireSlice';
import projectTypeReducer from './projectTypeSlice'; // ✅ PROJECT TYPE SLICE
import panelConfigReducer from './panelConfigSlice'; // ✅ PANEL CONFIG SLICE
import panelDetailReducer from './panelDetailSlice'; // ✅ PANEL DETAIL SLICE
import baslikReducer from './baslikSlice'; // ✅ BASLIK SLICE
import baslikTemplateReducer from './baslikTemplateSlice'; // ✅ BASLIK TEMPLATE SLICE
import energyRoomReducer from './energyRoomSlice'; // ✅ ENERGY ROOM SLICE
import projectTabsReducer from './projectTabsSlice'; // ✅ PROJECT TABS SLICE
import projectReducer from './projectSlice'; // ✅ PROJECT IDENTITY SLICE
import editorConfigReducer from './editorConfigSlice'; // ✅ EDITOR CONFIG SLICE
import tesisatReducer from './tesisatSlice'; // ✅ TESISAT SLICE (mesh rebuild meta layer)
import documentLayoutReducer from './documentLayoutSlice'; // ✅ DOC LAYOUT SLICE (manuel doc sıralaması)
import panelVisibilityReducer from './panelVisibilitySlice'; // ✅ PANEL VISIBILITY (Kolon/Pano/Başlık/Kablo Bacası show-hide)

// Global reset action
export const resetAllState = createAction('RESET_ALL_STATE');

// Global hydrate action — payload: { sliceKey: serializedData, ... }
export const hydrateProject = createAction('HYDRATE_PROJECT');

// Wrapper function to handle global reset + hydration
const createResettableReducer = (reducer, initialState, sliceKey) => {
  return (state, action) => {
    if (action.type === resetAllState.type) {
      return initialState;
    }
    if (action.type === hydrateProject.type && action.payload[sliceKey]) {
      // Merge serialized data onto initial state (preserves missing transient fields)
      return { ...initialState, ...action.payload[sliceKey] };
    }
    return reducer(state, action);
  };
};

// Get initial states from reducers
const operationInitialState = operationReducer(undefined, { type: '@@INIT' });
const modsInitialState = modsReducer(undefined, { type: '@@INIT' });
const selectionInitialState = selectionReducer(undefined, { type: '@@INIT' });
const gizmoInitialState = gizmoReducer(undefined, { type: '@@INIT' });
const snapInitialState = snapReducer(undefined, { type: '@@INIT' });
const dxfInitialState = dxfReducer(undefined, { type: '@@INIT' });
const layerInitialState = layerReducer(undefined, { type: '@@INIT' });
const operationHistoryInitialState = operationHistoryReducer(undefined, { type: '@@INIT' });
const groundingInitialState = groundingReducer(undefined, { type: '@@INIT' });
const simpleSupplyInitialState = simpleSupplyReducer(undefined, { type: '@@INIT' });
const utilsInitialState = utilsReducer(undefined, { type: '@@INIT' });
const settingsInitialState = settingsReducer(undefined, { type: '@@INIT' });
const floorPlanInitialState = floorPlanReducer(undefined, { type: '@@INIT' });
const wireInitialState = wireReducer(undefined, { type: '@@INIT' });
const projectTypeInitialState = projectTypeReducer(undefined, { type: '@@INIT' });
const panelConfigInitialState = panelConfigReducer(undefined, { type: '@@INIT' });
const panelDetailInitialState = panelDetailReducer(undefined, { type: '@@INIT' });
const baslikInitialState = baslikReducer(undefined, { type: '@@INIT' });
const baslikTemplateInitialState = baslikTemplateReducer(undefined, { type: '@@INIT' });
const energyRoomInitialState = energyRoomReducer(undefined, { type: '@@INIT' });
const tesisatInitialState = tesisatReducer(undefined, { type: '@@INIT' });
const documentLayoutInitialState = documentLayoutReducer(undefined, { type: '@@INIT' });

export const store = configureStore({
  reducer: {
    operation: createResettableReducer(operationReducer, operationInitialState, 'operation'),
    mods: createResettableReducer(modsReducer, modsInitialState, 'mods'),
    selection: createResettableReducer(selectionReducer, selectionInitialState, 'selection'),
    gizmo: createResettableReducer(gizmoReducer, gizmoInitialState, 'gizmo'),
    snap: createResettableReducer(snapReducer, snapInitialState, 'snap'),
    dxf: createResettableReducer(dxfReducer, dxfInitialState, 'dxf'),
    layer: createResettableReducer(layerReducer, layerInitialState, 'layer'),
    operationHistory: createResettableReducer(operationHistoryReducer, operationHistoryInitialState, 'operationHistory'),
    grounding: createResettableReducer(groundingReducer, groundingInitialState, 'grounding'),
    simpleSupply: createResettableReducer(simpleSupplyReducer, simpleSupplyInitialState, 'simpleSupply'),
    utils: createResettableReducer(utilsReducer, utilsInitialState, 'utils'),
    settings: createResettableReducer(settingsReducer, settingsInitialState, 'settings'),
    floorPlans: createResettableReducer(floorPlanReducer, floorPlanInitialState, 'floorPlans'),
    wires: createResettableReducer(wireReducer, wireInitialState, 'wires'),
    projectType: createResettableReducer(projectTypeReducer, projectTypeInitialState, 'projectType'),
    panelConfig: createResettableReducer(panelConfigReducer, panelConfigInitialState, 'panelConfig'),
    panelDetail: createResettableReducer(panelDetailReducer, panelDetailInitialState, 'panelDetail'),
    baslik: createResettableReducer(baslikReducer, baslikInitialState, 'baslik'),
    baslikTemplate: baslikTemplateReducer, // Kullanıcı şablonları (kullanıcı-seviyesi, localStorage backed) — resetAllState ile sıfırlanmaz
    energyRoom: createResettableReducer(energyRoomReducer, energyRoomInitialState, 'energyRoom'),
    tesisat: createResettableReducer(tesisatReducer, tesisatInitialState, 'tesisat'),
    documentLayout: createResettableReducer(documentLayoutReducer, documentLayoutInitialState, 'documentLayout'),
    projectTabs: projectTabsReducer, // Tab state proje geneli — resetAllState ile sıfırlanmaz
    project: projectReducer, // Proje kimlik state'i — resetAllState ile sıfırlanmaz
    editorConfig: editorConfigReducer, // Disiplin & araç config — resetAllState ile sıfırlanmaz
    panelVisibility: panelVisibilityReducer, // Panel show/hide (UI state) — resetAllState ile sıfırlanmaz
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // Three.js Vector3 ve diğer non-serializable objeler için
      immutableCheck: false,   // Three.js objeleri circular ref içerir — trackProperties stack overflow verir
    }),
});

// DEBUG: console'dan store erisimine izin ver
if (typeof window !== 'undefined') window.__store = store;
